import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api, logout } from '../utils/api';
import { 
  LayoutDashboard, 
  CreditCard, 
  Settings, 
  LogOut, 
  Menu, 
  X, 
  Brain, 
  Zap,
  Globe,
  Bell,
  ArrowUpRight,
  ShieldAlert,
  Wallet,
  CheckCircle2,
  Briefcase,
  User,
  Activity,
  Package,
  Clock,
  Upload,
  Image as ImageIcon,
  DollarSign
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';

export const UserDashboard = () => {
  const [activeTab, setActiveTab] = useState('servicios');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserAndOrders = async () => {
      try {
        const { data: userData } = await api.auth.getMe();
        setUser(userData);
        
        if (userData) {
          const { data: ordersData } = await api.orders.getByUserId(userData.id);
          setOrders(ordersData);
        }
      } catch (error) {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    fetchUserAndOrders();
  }, [navigate]);

  const handleLogout = () => {
    logout();
    toast.success('Protocolo de salida completado.');
    navigate('/');
  };

  const handleReceiptUpload = async (orderId, file, type) => {
    try {
      toast.loading('Sincronizando recibo...');
      await api.orders.uploadReceipt(orderId, file, type);
      toast.success('Recibo sincronizado correctamente.');
      
      // Refresh orders
      const { data } = await api.orders.getByUserId(user.id);
      setOrders(data);
    } catch (error) {
       console.error('Upload error:', error);
       toast.error('Error al subir el comprobante.');
    } finally {
      toast.dismiss();
    }
  };

  const menuItems = [
    { id: 'servicios', label: 'Ecosistema Activo', icon: Package },
    { id: 'pagos', label: 'Flujo Financiero', icon: Wallet },
    { id: 'ajustes', label: 'Núcleo Config', icon: Settings },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'TRABAJO FINALIZADO': return 'text-[#C6FF00] bg-[#C6FF00]/10 border-[#C6FF00]/20';
      case 'PENDIENTE': return 'text-white/20 bg-white/5 border-white/10';
      case 'CANCELADO': return 'text-red-500 bg-red-500/10 border-red-500/20';
      default: return 'text-blue-400 bg-blue-400/10 border-blue-400/20';
    }
  };

  return (
    <div className="min-h-screen bg-black text-white font-sans selection:bg-[#C6FF00] selection:text-black antialiased">
      {/* Sidebar */}
      <aside className={`fixed top-0 left-0 h-full w-64 bg-black border-r border-white/5 z-50 transition-transform duration-500 ease-out flex flex-col ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0`}>
        <div className="p-6 border-b border-white/5 bg-gradient-to-br from-white/[0.01] to-transparent shrink-0">
          <Link to="/" className="flex items-center space-x-3 group mb-8">
            <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center border border-white/10 group-hover:border-[#C6FF00]/40 transition-all">
              <Brain size={18} className="text-white group-hover:text-[#C6FF00] transition-colors" />
            </div>
            <div className="flex flex-col">
              <span className="text-base font-black tracking-tighter leading-none italic uppercase">MY NEXUS</span>
              <span className="text-[7px] font-black tracking-[0.4em] text-[#C6FF00] leading-none mt-1 uppercase">Central Node</span>
            </div>
          </Link>
          
          <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-3 opacity-10"><Activity size={12} className="text-[#C6FF00]" /></div>
            <p className="text-[7px] font-black uppercase tracking-[0.2em] text-white/20 mb-1 italic">IDENT_UNIT</p>
            <h4 className="text-[10px] font-bold uppercase text-white truncate max-w-[160px]">{user?.email || 'N/A'}</h4>
            <div className="mt-3 flex items-center gap-1.5">
               <div className="w-1 h-1 rounded-full bg-[#C6FF00] animate-pulse"></div>
               <span className="text-[7px] font-black uppercase tracking-widest text-[#C6FF00]/80">Status: ONLINE</span>
            </div>
          </div>
        </div>

        <nav className="flex-1 overflow-y-auto p-4 py-6 space-y-2">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => { setActiveTab(item.id); setIsSidebarOpen(false); }}
              className={`w-full flex items-center space-x-4 px-6 py-4 rounded-xl transition-all duration-300 group ${
                activeTab === item.id 
                  ? 'bg-[#C6FF00]/5 text-[#C6FF00] border border-[#C6FF00]/20' 
                  : 'text-white/20 hover:bg-white/[0.02] hover:text-white border border-transparent'
              }`}
            >
              <item.icon size={16} className={`${activeTab === item.id ? 'text-[#C6FF00]' : 'group-hover:text-white'} transition-colors`} />
              <span className="text-[9px] font-black uppercase tracking-[0.2em] text-left">{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="p-6 border-t border-white/5">
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-4 px-6 py-4 rounded-xl text-white/10 hover:bg-red-500/10 hover:text-red-500 border border-transparent transition-all group"
          >
            <LogOut size={16} />
            <span className="text-[9px] font-black uppercase tracking-[0.2em]">Cerrar Nodo</span>
          </button>
        </div>
      </aside>

      <main className="lg:ml-64 min-h-screen relative flex flex-col">
        {/* Header */}
        <header className="sticky top-0 z-40 bg-black/80 backdrop-blur-3xl border-b border-white/5 p-4 sm:p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
             <button onClick={() => setIsSidebarOpen(true)} className="lg:hidden p-2 text-white/40 hover:text-white bg-white/5 rounded-lg border border-white/10"><Menu size={18} /></button>
             <Link to="/" className="text-[10px] font-black uppercase tracking-widest text-white/20 hover:text-white transition-colors flex items-center gap-2">
                <ArrowUpRight size={14} className="rotate-[270deg]" /> Portal Principal
             </Link>
          </div>

          <div className="flex items-center gap-4">
             <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#C6FF00]/10 to-transparent border border-[#C6FF00]/20 flex items-center justify-center shadow-lg">
                <User size={16} className="text-[#C6FF00]" />
             </div>
          </div>
        </header>

        {/* Content */}
        <div className="flex-1 p-6 sm:p-10 max-w-5xl mx-auto w-full space-y-10">
          <AnimatePresence mode="wait">
            {activeTab === 'servicios' && (
              <motion.div key="servicios" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-10">
                <div className="flex items-center justify-between border-b border-white/5 pb-8">
                   <div>
                      <span className="text-[#C6FF00] font-black uppercase tracking-[0.4em] text-[8px] block mb-1 italic">Project_Pipeline</span>
                      <h2 className="text-3xl font-black uppercase italic tracking-tight">Ecosistema Activo</h2>
                   </div>
                   <Link to="/" className="px-6 py-3 bg-white/5 border border-white/10 text-white font-black rounded-lg text-[9px] uppercase tracking-widest hover:bg-white hover:text-black transition-all">Integrar Nodo</Link>
                </div>

                {orders.length === 0 ? (
                  <div className="py-20 text-center border border-white/5 border-dashed rounded-[2.5rem] bg-white/[0.01]">
                     <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Package size={28} className="text-white/10" />
                     </div>
                     <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/20 italic">No se detectan procesos activos en este hilo.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {orders.map(order => (
                      <div key={order.id} className="group p-8 rounded-[2rem] bg-[#0A0A0A] border border-white/5 hover:border-[#C6FF00]/20 transition-all shadow-2xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 p-6 opacity-0 group-hover:opacity-[0.03] transition-opacity"><Zap size={40} className="text-[#C6FF00]" /></div>
                        
                        <div className="flex items-center justify-between mb-8">
                           <div className={`px-4 py-2 rounded-full text-[7px] font-black tracking-widest uppercase border ${getStatusColor(order.status)}`}>
                             {order.status}
                           </div>
                           <span className="text-[10px] font-black text-white/10 tracking-widest italic uppercase">{new Date(order.created_at).toLocaleDateString()}</span>
                        </div>

                        <h3 className="text-xl font-black uppercase italic tracking-tight mb-2 group-hover:text-[#C6FF00] transition-colors">{order.services?.title}</h3>
                        <p className="text-white/30 text-[9px] font-black uppercase tracking-widest mb-10 italic">ID_PROJ: {order.id.slice(0, 8).toUpperCase()}</p>

                        {/* Progress Bar */}
                        <div className="space-y-3 mb-10">
                           <div className="flex items-center justify-between text-[7px] font-black uppercase tracking-widest text-white/20">
                              <span>Pipeline Progression</span>
                              <span className="text-[#C6FF00]/40">Status: {order.status}</span>
                           </div>
                           <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden">
                              <motion.div 
                                initial={{ width: 0 }}
                                animate={{ width: order.status === 'TRABAJO FINALIZADO' ? '100%' : order.status === 'PENDIENTE' ? '10%' : '50%' }}
                                className="h-full bg-[#C6FF00] shadow-[0_0_10px_rgba(198,255,0,0.5)]"
                              />
                           </div>
                        </div>

                        <div className="pt-6 border-t border-white/5 flex items-center justify-between">
                           <div className="flex items-center gap-2 text-[#C6FF00]/40 group-hover:text-[#C6FF00] transition-colors">
                              <Activity size={12} />
                              <span className="text-[8px] font-black uppercase tracking-widest italic">Hilo de Comunicación Activo</span>
                           </div>
                           <ArrowUpRight size={16} className="text-white/10 group-hover:text-white transition-colors" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            )}

            {activeTab === 'pagos' && (
              <motion.div key="pagos" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-10">
                 <div className="border-b border-white/5 pb-8">
                    <span className="text-[#C6FF00] font-black uppercase tracking-[0.4em] text-[8px] block mb-1 italic">Financial_Module</span>
                    <h2 className="text-3xl font-black uppercase italic tracking-tight">Flujo Financiero</h2>
                 </div>

                 <div className="space-y-6">
                    {orders.filter(o => o.advance_amount > 0 || o.final_amount > 0).length === 0 ? (
                      <div className="py-20 text-center border border-white/5 border-dashed rounded-[2.5rem] bg-white/[0.01]">
                        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-white/20 italic">No se han definido montos de pago para tus servicios activos.</p>
                      </div>
                    ) : (
                      orders.map(order => (
                        <div key={order.id} className="p-10 rounded-[2.5rem] bg-[#0A0A0A] border border-white/5 relative overflow-hidden shadow-2xl">
                           <div className="flex flex-col md:flex-row md:items-center justify-between gap-10 mb-12">
                              <div>
                                 <p className="text-[11px] font-black uppercase italic text-[#C6FF00] mb-2 tracking-widest">{order.services?.title}</p>
                                 <h4 className="text-5xl font-black italic tracking-tighter text-white">
                                    ${(parseFloat(order.advance_amount) + parseFloat(order.final_amount)).toFixed(2)} 
                                    <span className="text-xs font-medium not-italic opacity-20 tracking-widest ml-4">ESTIMADO TOTAL</span>
                                 </h4>
                              </div>
                              <div className="grid grid-cols-2 gap-4">
                                 <div className="p-4 bg-white/[0.02] rounded-xl border border-white/5 text-center">
                                    <p className="text-[7px] font-black text-white/20 uppercase mb-1">Advance Phase</p>
                                    <p className="text-sm font-black italic">${order.advance_amount}</p>
                                 </div>
                                 <div className="p-4 bg-white/[0.02] rounded-xl border border-white/5 text-center">
                                    <p className="text-[7px] font-black text-white/20 uppercase mb-1">Final Phase</p>
                                    <p className="text-sm font-black italic">${order.final_amount}</p>
                                 </div>
                              </div>
                           </div>

                           <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-10 border-t border-white/5">
                              {/* Advance Payment Upload */}
                              <div className="space-y-4">
                                 <div className="flex items-center justify-between">
                                    <h5 className="text-[10px] font-bold uppercase tracking-widest text-white italic">1. Pago Adelanto</h5>
                                    {order.advance_receipt_url && <span className="text-[#C6FF00] flex items-center gap-1 text-[7px] font-bold italic uppercase"><CheckCircle2 size={10} /> Enviado</span>}
                                 </div>
                                 <div className="relative group p-6 rounded-2xl bg-white/[0.01] border border-white/5 border-dashed hover:border-[#C6FF00]/40 transition-all text-center">
                                    <input 
                                       type="file" 
                                       onChange={(e) => handleReceiptUpload(order.id, e.target.files[0], 'advance')}
                                       className="absolute inset-0 opacity-0 cursor-pointer" 
                                    />
                                    <Upload size={20} className="mx-auto mb-4 text-white/20 group-hover:text-[#C6FF00] transition-colors" />
                                    <p className="text-[9px] font-black uppercase tracking-widest text-white/20">Subir Comprobante Adelanto</p>
                                 </div>
                              </div>

                              {/* Final Payment Upload */}
                              <div className="space-y-4">
                                 <div className="flex items-center justify-between">
                                    <h5 className="text-[10px] font-bold uppercase tracking-widest text-white italic">2. Liquidación Final</h5>
                                    {order.final_receipt_url && <span className="text-[#C6FF00] flex items-center gap-1 text-[7px] font-bold italic uppercase"><CheckCircle2 size={10} /> Enviado</span>}
                                 </div>
                                 <div className="relative group p-6 rounded-2xl bg-white/[0.01] border border-white/5 border-dashed hover:border-[#C6FF00]/40 transition-all text-center">
                                    <input 
                                       type="file" 
                                       onChange={(e) => handleReceiptUpload(order.id, e.target.files[0], 'final')}
                                       className="absolute inset-0 opacity-0 cursor-pointer" 
                                    />
                                    <Upload size={20} className="mx-auto mb-4 text-white/20 group-hover:text-[#C6FF00] transition-colors" />
                                    <p className="text-[9px] font-black uppercase tracking-widest text-white/20">Subir Comprobante Final</p>
                                 </div>
                              </div>
                           </div>
                        </div>
                      ))
                    )}
                 </div>
              </motion.div>
            )}

            {activeTab === 'ajustes' && (
              <motion.div key="ajustes" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-10">
                 <div className="border-b border-white/5 pb-8">
                    <span className="text-[#C6FF00] font-black uppercase tracking-[0.4em] text-[8px] block mb-1 italic">Security_Layer</span>
                    <h2 className="text-3xl font-black uppercase italic tracking-tight">Núcleo Config</h2>
                 </div>

                 <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
                    <div className="md:col-span-3 p-8 sm:p-10 rounded-[2.5rem] bg-[#0A0A0A] border border-white/5 space-y-8">
                       <h4 className="text-[8px] font-black uppercase tracking-[0.4em] text-[#C6FF00]/40 mb-6 italic italic">Credenciales</h4>
                       <div className="space-y-6">
                          <div className="space-y-2">
                             <label className="text-[7px] font-black uppercase tracking-widest text-white/10 italic ml-1">Email_Registry</label>
                             <div className="p-4 bg-white/[0.03] rounded-lg border border-white/5 text-[9px] font-black uppercase text-white/40">{user?.email || 'DEBUG@IO'}</div>
                          </div>
                          <div className="space-y-2 pt-6">
                             <label className="text-[7px] font-black uppercase tracking-widest text-white/10 italic ml-1">Update_Secret</label>
                             <input type="password" placeholder="••••••••" className="w-full bg-white/[0.03] border border-white/5 rounded-lg p-4 text-[9px] font-black uppercase tracking-widest text-white focus:border-[#C6FF00]/20 outline-none transition-all" />
                          </div>
                          <button className="w-full py-5 bg-white/5 text-white/40 rounded-lg font-black uppercase tracking-widest text-[9px] hover:bg-white/10 hover:text-white transition-all border border-white/5 mt-6 italic">Sincronizar Credenciales</button>
                       </div>
                    </div>

                    <div className="md:col-span-2 p-8 sm:p-10 rounded-[2.5rem] bg-red-500/[0.01] border border-red-500/5 flex flex-col items-center justify-center text-center space-y-6">
                       <div className="w-16 h-16 rounded-2xl bg-red-500/5 border border-red-500/10 flex items-center justify-center text-red-500/40">
                          <ShieldAlert size={28} />
                       </div>
                       <h4 className="text-sm font-black uppercase tracking-tight">Zona Crítica</h4>
                       <p className="text-white/20 text-[8px] font-medium max-w-[160px] uppercase tracking-widest italic leading-relaxed">Eliminar permanentemente la conexión con este nodo y todos sus activos.</p>
                       <button className="px-10 py-4 bg-red-500/10 text-red-500 border border-red-500/20 rounded-xl font-black uppercase tracking-widest text-[9px] hover:bg-red-500 hover:text-white transition-all shadow-xl">Purga Completa</button>
                    </div>
                 </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#C6FF00]/5 blur-[150px] pointer-events-none"></div>
      </main>

      {isSidebarOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} onClick={() => setIsSidebarOpen(false)} className="fixed inset-0 bg-black/90 backdrop-blur-md z-40 lg:hidden" />
      )}
    </div>
  );
};
